import { ChangeDetectorRef, Component, OnInit, OnDestroy, NgZone, HostListener } from '@angular/core';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { Router } from '@angular/router';
import { AuthService } from './services/auth.service';
import { CompanyService } from './services/company/company.service';
import { EmployeeListService } from './services/employee/employee-list.service';
import { IdleService } from './services/idle.service';
import { WidgetService } from './services/widget.service';
import { LanguageService } from './services/language.service';
import { ToastService } from './services/toast.service';
import { FormChangeTrackerService } from './services/form-change-tracker.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'hrm-fornend';
  idleState = 'NOT_STARTED';
  countdown?: number = 0;
  lastPing: Date = new Date();
  private authSubscription: any;
  private idleTimeoutSubscription: any;
  private timeoutId: any;
  private timeout = 5000; // 5 seconds
  private activityHandler: any;
  private events = ['mousemove', 'keydown', 'click', 'touchstart', 'api_activity'];

  userString = localStorage.getItem('auth_user');
  user = this.userString ? JSON.parse(this.userString) : null;

  companyTimeOut = 60 * 10;
  isFirstLogin: number = 0;

  constructor(
    private router: Router,
    private authService: AuthService,
    private companyService: CompanyService,
    private employeeService: EmployeeListService,
    private readonly idleService: IdleService,
    private widgetService: WidgetService,
    private langService: LanguageService,
    private ngZone: NgZone,
    private toastService: ToastService,
    private formTracker: FormChangeTrackerService,
  ) { }

  // @HostListener('window:beforeunload', ['$event'])
  // handleBeforeUnload(event: any) {
  //   const isLoginPage = this.router.url === '/' || this.router.url === '/login';
  //   if (!isLoginPage && this.formTracker.isAnyFormDirty()) {
  //     event.returnValue = true;
  //   }
  // }

  ngOnInit(): void {
    let colorPallette = localStorage.getItem('color');
    if (colorPallette) {
      const color = JSON.parse(colorPallette);

      if (color) {
        document.documentElement.style.setProperty('--header-color', color?.bg_color);
        document.documentElement.style.setProperty('--header-text-color', color?.bg_text_color);
        document.documentElement.style.setProperty('--sidebar-text-color', color?.sidebar_text_color);
        document.documentElement.style.setProperty('--sidebar-color', color?.sidebar_color);
        document.documentElement.style.setProperty('--link-active-bg-color', color?.link_active_bg_color);
        document.documentElement.style.setProperty('--link-active-text-color', color?.link_active_text_color);
        document.documentElement.style.setProperty('--title-color', color?.title_color);
        document.documentElement.style.setProperty('--link-hover-bg-color', color?.link_hover_bg_color);
        document.documentElement.style.setProperty('--link-hover-text-color', color?.link_hover_text_color);
        // this.widgetService.getColor(color?.id).subscribe({
        //   next: resp => {
        //     console.log(resp);
        //     document.documentElement.style.setProperty('--header-color', resp?.data?.bg_color);
        //     document.documentElement.style.setProperty('--header-text-color', resp?.data?.bg_text_color);
        //     document.documentElement.style.setProperty('--sidebar-text-color', resp?.data?.sidebar_text_color);
        //     document.documentElement.style.setProperty('--sidebar-color', resp?.data?.sidebar_color);
        //   }
        // })
      }
      else {
        document.documentElement.style.setProperty('--header-color', '#2A9D8E');
        document.documentElement.style.setProperty('--header-text-color', '#ffffff');
        document.documentElement.style.setProperty('--sidebar-text-color', '#ffffff');
        document.documentElement.style.setProperty('--sidebar-color', '#2f4050');
        document.documentElement.style.setProperty('--link-active-bg-color', '#e0e0e0');
        document.documentElement.style.setProperty('--link-active-text-color', '#006fc2');
        document.documentElement.style.setProperty('--title-color', '#000000');
        document.documentElement.style.setProperty('--link-hover-text-color', '#006fc2');
      }
    }

    // Subscribe to user changes so idle timeout is set up on login and torn down on logout
    this.authSubscription = this.authService.user.subscribe((user: any) => {
      if (user?.companyID) {
        this.companyService.getParametersById(user.companyID).subscribe({
          next: (resp: any) => {
            const timeoutMinutes = resp?.data?.idle_time_out ? Number(resp.data.idle_time_out) : 0;
            if (!isNaN(timeoutMinutes) && timeoutMinutes > 0) {
              this.companyTimeOut = timeoutMinutes;
              this.setupIdleTimeout(timeoutMinutes);
            } else {
              console.log('[AutoLogout] Inactivity logout disabled: No idle_time_out configured in company params.');
            }
          }
        });
      } else {
        // User logged out — clear the timer and listeners
        this.removeActivityListeners();
        if (this.timeoutId) {
          clearTimeout(this.timeoutId);
          this.timeoutId = null;
        }
      }
    });

    // Re-setup idle timeout immediately when company parameters are saved with a new value
    this.idleTimeoutSubscription = this.companyService.idleTimeoutUpdated$.subscribe((timeoutMinutes: number) => {
      this.companyTimeOut = timeoutMinutes;
      this.setupIdleTimeout(timeoutMinutes);
    });
  }

  setupIdleTimeout(timeoutMinutes: number) {
    const timeoutMs = timeoutMinutes * 60 * 1000;

    console.log(`[AutoLogout] Setup for ${timeoutMinutes} minutes`);

    // Remove old listeners (IMPORTANT)
    this.removeActivityListeners();

    // Create single handler reference
    this.activityHandler = this.resetTimer.bind(this, timeoutMs);

    // Add listeners
    this.events.forEach(event => {
      window.addEventListener(event, this.activityHandler);
    });

    // Start timer
    this.resetTimer(timeoutMs);
  }

  resetTimer(timeoutMs: number) {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.timeoutId = setTimeout(() => {
      this.autoLogout();
    }, timeoutMs);
  }

  autoLogout() {
    const isLoginPage = this.router.url === '/' || this.router.url === '/login';
    if (isLoginPage) return;

    if ((window as any).activeApiRequests > 0) {
      // console.log("[AutoLogout] Auto logout deferred: API requests are still active.");
      this.resetTimer(30 * 1000); // Check again in 30 seconds
      return;
    }

    // console.log("[AutoLogout] Auto logout triggered due to inactivity at ", new Date().toLocaleTimeString());
    this.authService.logout().subscribe({
      next: (resp) => {
        // Logged out on backend
      },
    });
    localStorage.removeItem('auth_user');
    localStorage.removeItem('menus');
    localStorage.removeItem('default_company');
    localStorage.removeItem('selectedMenuID');
    this.authService.loggedIn.next(false);
    
    if (!isLoginPage) {
      this.router.navigate(['/']);

      setTimeout(() => {
        this.toastService.failure("Your session has expired due to inactivity.");
      }, 100);
    }
  }

  removeActivityListeners() {
    if (this.activityHandler) {
      this.events.forEach(event => {
        window.removeEventListener(event, this.activityHandler);
      });
    }
  }

  ngOnDestroy(): void {
    this.removeActivityListeners();

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }

    if (this.idleTimeoutSubscription) {
      this.idleTimeoutSubscription.unsubscribe();
    }
  }
}
